import ArrowLink from "components/links/ArrowLink";
import { GameItem } from "interfaces";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, utils } from "services";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Card = ({ status, itemType, value = 0 }: { status: string, itemType: keyof typeof GameItem, value?: number }) => {
  const getDisplayName = () => {
    switch (itemType) {
      case "mag":
        return "MAG";
      case "mstr":
        return "MSTR";
      case "evo-item":
        return "EVO pts";
    }
  }
  return (
    <div className="bg-black-secondary h-[140px] rounded-lg p-6">
      <div className="flex">
        {status === "off-chain" && <div className="custom-btn-color-yellow rounded-md pr-3 pl-3 pt-0.5 text-center text-sm mr-3">
          Off-chain
        </div>}
        {status === "on-chain" && <div className="custom-btn-color-blue rounded-md pr-3 pl-3 pt-0.5 text-center text-sm mr-3">
          On-chain
        </div>}
        {status === "total" && <div className="custom-btn-color-purple-red rounded-md pr-3 pl-3 pt-0.5 text-center text-sm mr-3">
          Total
        </div>}
        <div>
          <img src={`/assets/images/items/${itemType}-icon.svg`} alt={`${itemType}`} className="inline-block mr-1 h-6 w-6 " />
          {getDisplayName()}
        </div>
      </div>
      <div className="text-2xl main-font-medium mt-9">
        {utils.convertToInternationalCurrencySystem(Number(value)) ?? "0"}
      </div>
    </div>
  )
}

export default function DetailFarmerPage() {
  const { identity } = useParams();
  const [data, setData] = useState(null);
  const [magChartState, setMagChart] = useState({
    labels: [],
    datasets: [
      {
        label: "MAG",
        backgroundColor: 'rgba(75,192,192,1)',
        borderColor: 'rgba(0,0,0,1)',
        borderWidth: 2,
        data: []
      }
    ]
  });
  const [evoChartState, setEvoChart] = useState({
    labels: [],
    datasets: [
      {
        label: "Evolution items",
        backgroundColor: '#8D2DD5',
        borderColor: 'rgba(0,0,0,1)',
        borderWidth: 2,
        data: []
      }
    ]
  });

  useEffect(() => {
    api.getDetailFarmer(identity).then((data) => {
      setData(data);

      const { dailyMag, dailyEvol } = data;
      const magChartKey = Object.keys(dailyMag).map((key: string) => key.replace("_", "/").slice(0, 5));
      const magChartValue = Object.keys(dailyMag).map((key: string) => Number(dailyMag[key]));
      const evolChartKey = Object.keys(dailyEvol).map((key: string) => key.replace("_", "/").slice(0, 5));;
      const evolChartValue = Object.keys(dailyEvol).map((key: string) => Number(dailyEvol[key]));

      setMagChart({
        labels: magChartKey ?? [],
        datasets: [
          {
            label: "MAG",
            backgroundColor: 'rgba(75,192,192,1)',
            borderColor: 'rgba(0,0,0,1)',
            borderWidth: 2,
            data: magChartValue
          }
        ]
      })

      setEvoChart({
        labels: evolChartKey ?? [],
        datasets: [
          {
            label: "Evolution items",
            backgroundColor: '#8D2DD5',
            borderColor: 'rgba(0,0,0,1)',
            borderWidth: 2,
            data: evolChartValue
          }
        ]
      })


    }).catch(console.log)
  }, [])


  return (
    <div className="w-full text-white dao-container">
      <ArrowLink direction="left" href='/#/account'>
        Back
      </ArrowLink>
      <div className="text-2xl mt-6">
        {identity}
      </div>
      <div className="text-gray mt-2 text-sm">
        <div>
          Last login: {data?.lastLogin ? `${new Date(data?.lastLogin ?? 0)}` : "--/--/--"}
        </div>
        <div>
          Location: {data?.lastLocation ? data.lastLocation + "(Approximate)" : "--"}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-4 gap-6">
        <div className="grid col-span-2 grid-cols-1 gap-6">
          <Card status="off-chain" itemType="mag" value={data?.total?.mag} />
          <Card status="off-chain" itemType="mstr" value={data?.total?.mstr} />
          <Card status="off-chain" itemType="evo-item" value={data?.total?.evol} />
        </div>

        <div className="bg-black-secondary col-span-2 h-[460px] rounded-lg p-6">
          <div className="">
            <img src="/assets/images/items/mag-icon.webp" alt="mag" className="inline-block mr-2 w-6 h-6" />
            MAG
          </div>
          <div className="flex justify-between py-3">
            <div>[Today]</div>
            <div>{data?.today?.mag ?? "0"}</div>
          </div>
          <div className="br" />
          <div className="flex justify-between py-3">
            <div>[Yesterday]</div>
            <div>{data?.yesterday?.mag ?? "0"}</div>
          </div>

          <div className="mt-4">
            <img src="/assets/images/items/evo-item-icon.svg" alt="evo" className="inline-block mr-2 w-6 h-6" />
            EVO pts
          </div>
          <div className="flex justify-between py-3">
            <div>[Today]</div>
            <div>{data?.today?.evol ?? "0"}</div>
          </div>
          <div className="br" />
          <div className="flex justify-between py-3">
            <div>[Yesterday]</div>
            <div>{data?.yesterday?.evol ?? "0"}</div>
          </div>

          <div className="br-2 -mx-6 mt-2 mb-5" />
          {[
            { title: "Mongens", field: "mongens" },
            { title: "Soulcores", field: "soulcores" },
            { title: "Plots", field: "plots" },
            { title: "Blueprints", field: "blueprints" },
            { title: "EVO potions", field: "evol" }
          ].map((item: { title: string, field: string }, i: number) => {
            return (
              <div className="flex justify-between py-2" key={item?.title + i}>
                <div className="flex">
                  <div className="custom-btn-color-purple-red rounded-md pr-3 pl-3 pt-1 text-center text-sm mr-3">
                    Total
                  </div>
                  <span>
                    <img src={`/assets/images/items/${item.title.toString().toLocaleLowerCase().replace(" ", "").slice(0, -1)}-icon.svg`} alt="icon" className="w-6 h-6 inline-block mr-1" />
                    {item.title}
                  </span>
                </div>
                <div>{data?.total[item.field] ?? "0"}</div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-10">
        <div>
          <div>
            <img src="/assets/images/items/mag-icon.webp" alt="mag" className="inline-block mr-2 mb-1 h-8 w-8 " />
            Daily MAG
          </div>
          <div className="bg-black-secondary rounded-lg mt-1 relative p-6">
            <div className="">
              {magChartState &&
                <Bar
                  data={magChartState}
                  options={{
                    //@ts-ignore
                    title: {
                      display: true,
                      fontSize: 20
                    },
                    legend: {
                      display: true,
                      position: 'right'
                    }
                  }}
                />
              }
            </div>
            <div className="text-yellow mt-6 mr-6 px-3">
              *A day with a value of 0 means that you did a withdraw on that day and we cannot determine its true value
            </div>
          </div>
        </div>

        <div>
          <div>
            <img src="/assets/images/items/evo-item-icon.svg" alt="evo item" className="inline-block mr-2 mb-1 h-8 w-8 " />
            Daily EVO points
          </div>
          <div className="bg-black-secondary rounded-lg mt-1 relative p-6">
            {evoChartState && <Bar
              data={evoChartState}
              options={{
                //@ts-ignore
                title: {
                  display: true,
                  fontSize: 20
                },
                legend: {
                  display: true,
                  position: 'right'
                },
              }}
            />}
            <div className="text-yellow mr-6 mt-6 px-3">
              *A day with a value of 0 means that you did a withdraw on that day and we cannot determine its true value
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
